import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Keeps a resizable group from compressing its fixed (non-flex) panels when the window shrinks.
 *
 * react-resizable-panels renders the Group with `width: 100%; overflow: hidden` and always fits
 * its panels inside that width — so when the window gets narrower than the panels' combined width,
 * v4 squishes every panel to fit. We don't want that: only the flex panel (the gallery) should
 * shrink; the others must keep their width and let the row overflow the (clipped) parent, so the
 * rightmost panel slides under the window edge instead of shrinking.
 *
 * To achieve that we give the Group element a `min-width` equal to the combined width of the fixed
 * panels (everything except `flexPanelId`) plus the separators. When the container is narrower than
 * that, the Group overflows its overflow-hidden parent at its min-width — panels keep their size and
 * the overflow is clipped. When the container is wider, min-width has no effect and the flex panel
 * absorbs the extra space as usual.
 *
 * The flex panel is no longer excluded from the reservation: we also reserve `flexPanelMinWidth`
 * for it, so the center (gallery) keeps its own floor and can't be squeezed out of view when a
 * fixed panel is dragged wider or the window narrows.
 *
 * `minTotalWidth` is the fixed-panel floor. It seeds the value and guards the first paint / a narrow
 * initial load, where the panels would otherwise be measured while compressed.
 *
 * @param flexPanelId  id of the single panel allowed to grow/shrink (the gallery / main content)
 * @param minTotalWidth  sum of the rendered fixed panels' minSizes, in pixels
 * @param flexPanelMinWidth  minimum width reserved for the flex panel itself, in pixels (default 0)
 * @returns groupRef to attach to the Group element, and the minWidth to apply to its style
 */
export const useFixedPanelsGroupMinWidth = ({
  flexPanelId,
  minTotalWidth,
  flexPanelMinWidth = 0,
}: {
  flexPanelId: string;
  minTotalWidth: number;
  flexPanelMinWidth?: number;
}) => {
  const groupRef = useRef<HTMLDivElement | null>(null);
  const [minWidth, setMinWidth] = useState(minTotalWidth + flexPanelMinWidth);

  const recompute = useCallback(() => {
    const group = groupRef.current;
    if (!group) {
      return;
    }

    let fixedWidth = 0;
    Array.from(group.children).forEach((child) => {
      if (!(child instanceof HTMLElement)) {
        return;
      }
      if (child.hasAttribute('data-separator')) {
        fixedWidth += child.offsetWidth;
        return;
      }
      if (child.hasAttribute('data-panel') && child.id !== flexPanelId) {
        fixedWidth += child.offsetWidth;
      }
    });

    const next = Math.max(fixedWidth, minTotalWidth) + flexPanelMinWidth;
    setMinWidth((prev) => (prev === next ? prev : next));
  }, [flexPanelId, minTotalWidth, flexPanelMinWidth]);

  useLayoutEffect(() => {
    const group = groupRef.current;
    if (!group) {
      return;
    }
    recompute();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(() => recompute());
    observer.observe(group);
    if (group.parentElement) {
      // The clipped parent is what actually changes width when the window resizes.
      observer.observe(group.parentElement);
    }
    Array.from(group.children).forEach((child) => {
      if (child instanceof HTMLElement) {
        observer.observe(child);
      }
    });
    return () => observer.disconnect();
  }, [recompute]);

  return { groupRef, minWidth };
};
